import Button from '../buttons/button'
import { Counter, Tag } from '../badgesAndTags'
import {
	TypographyHeading,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import {
	CopyIcon,
	XIcon,
	CalloutSuccessIcon,
	CaretLeft,
	CalloutWarningIcon,
} from '../icons'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { useRef } from 'react'
import CollectionTemplate from '../common/metadataTemplates/collectionTemplate.tsx'
import CIDsTable from '../common/cidsTable.tsx'
import SignersTable from '../common/signersTable.tsx'
import {
	formatMetadataType,
	getCollectionProfileURL,
	getShareAttestationToXURL,
	mapIcon,
	mapSigners,
} from '#app/utils/misc.tsx'
import { Link } from '@remix-run/react'
import InternalLink from '../common/internalLink.tsx'
import { AttestationType, CollectionNFTEntry } from '#types/loreTypes.tsx'

const formatDate = (date: Date) => {
	try {
		const options = {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		}
		//@ts-ignore
		return date.toLocaleDateString('en-US', options)
	} catch (e) {
		return 'Unknown'
	}
}

const createCIDTableData = (
	metadataEntry: AttestationType,
	nfts: CollectionNFTEntry[],
) => {
	const tableData = []
	if (metadataEntry) {
		if (nfts && nfts.length > 0) {
			nfts
				// .filter(nftAttestation => nftAttestation.references.type != 'Rollup')
				.forEach(nft => {
					tableData.push({
						cid: undefined,
						referenceCid:
							metadataEntry.references.attestationIPFSCID || 'Pending',
						name: nft.name || 'Unknown name',
						format: nft.contentType ? nft.contentType : 'Unknown format',
						date: metadataEntry.createdAt,
						src: nft.thumbnailImage
							? nft.thumbnailImage
							: nft.image
								? nft.image
								: '',
						isVerified: undefined,
						chain: nft.chainName ? nft.chainName : '',
						contractAddress: nft.contractAddress ? nft.contractAddress : '',
						tokenId: nft.tokenId ? nft.tokenId : '',
						status: undefined,
						attestation: metadataEntry,
					})
				})
		}
		return tableData
	} else {
		return tableData
	}
}

export default function MetadataProfile({
	cid,
	collection,
	nftCount,
	nftMessage,
	nfts,
	atomicLoreUrl,
}: {
	cid: string
	collection: AttestationType
	nfts: CollectionNFTEntry[]
	nftMessage: string
	nftCount: number
	atomicLoreUrl: string
}) {
	const tooltipCopyIPFS = useRef<TooltipRefProps>(null)
	const tooltipCopyArweave = useRef<TooltipRefProps>(null)

	return (
		<div className="mx-auto mb-16 flex h-full w-full max-w-[1440px] flex-1 flex-col px-4 md:px-12 lg:px-40">
			<div className="hidden md:block">
				<div className="mb-8 mt-16 flex flex-col items-start justify-around gap-y-4 pl-0 md:items-start lg:justify-evenly">
					<InternalLink to={'/'} prefetch="none">
						<Button variant="tertiary">
							<div className="flex items-center">
								<CaretLeft />
								Home
							</div>
						</Button>
					</InternalLink>
				</div>
				<div className="flex w-full flex-row justify-between">
					{!collection.isRevoked && (
						<Tag
							variant="outline"
							size="small"
							icon={mapIcon(
								collection.template.slug,
								'large',
								collection.properties?.fileType || '',
							)}
							showIcon={true}
						>
							{formatMetadataType(collection.template.slug)}
						</Tag>
					)}
					<div className="flex flex-row items-center gap-x-2">
						{!collection.isRevoked && (
							<>
								<Link
									to={getShareAttestationToXURL(collection, atomicLoreUrl)}
									target="_blank"
								>
									<Button
										variant="secondary"
										leftIcon={<XIcon />}
										additionalClasses="w-full"
									>
										Share
									</Button>
								</Link>
								<InternalLink to={getCollectionProfileURL(collection)}>
									<Button variant="primary">View Collection Profile</Button>
								</InternalLink>
							</>
						)}
					</div>
				</div>
				<div className="mt-4 flex flex-row items-center justify-between">
					<TypographyHeading
						size="large"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>{`${collection.isRevoked ? 'Revoked ' : ''}Collection - ${collection.properties.name}`}</TypographyHeading>
				</div>
			</div>
			{!collection.isRevoked && (
				<div className="md:hidden">
					<div className="mt-16 w-fit">
						<Tag
							icon={mapIcon(
								collection.template.slug,
								'large',
								collection.properties?.fileType || '',
							)}
							showIcon={true}
						>
							{formatMetadataType(collection.template.slug)}
						</Tag>
					</div>
					<div className="mt-4 flex flex-row items-center justify-start">
						<TypographyHeading
							size="large"
							isBold={true}
						>{`${collection.isRevoked ? 'Revoked ' : ''}Collection`}</TypographyHeading>
					</div>
				</div>
			)}
			{!collection.isRevoked && collection.createdAt && (
				<div className="mt-4 flex flex-row items-center gap-x-2">
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Timestamp:{' '}
					</TypographyLabel>
					<TypographyLabel
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{formatDate(new Date(collection.createdAt))}
					</TypographyLabel>
				</div>
			)}
			{!collection.isRevoked && (
				<div className="mt-4 flex flex-row items-center gap-x-2">
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
					>
						IPFS CID:
					</TypographyLabel>
					<TypographyLabel
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark truncate"
					>
						{cid}
					</TypographyLabel>
					<div id="copyIPFS" className="flex flex-row">
						<CopyIcon
							className="h-4 w-4 text-textSecondaryLight hover:cursor-pointer dark:text-textSecondaryDark"
							onClick={() => {
								navigator.clipboard.writeText(cid)
								tooltipCopyIPFS.current?.open({
									anchorSelect: '#copyIPFS',
									content: 'Copied!',
								})
								setTimeout(() => {
									tooltipCopyIPFS.current?.close()
								}, 2000)
							}}
						/>
						<Tooltip imperativeModeOnly ref={tooltipCopyIPFS} />
					</div>
				</div>
			)}
			{!collection.isRevoked && (
				<div className="mt-4 flex flex-row items-center gap-x-2">
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
					>
						Arweave Transaction:
					</TypographyLabel>
					<TypographyLabel
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark truncate"
					>
						{collection.references.attestationArweaveTxId}
					</TypographyLabel>
					<div id="copyArweave" className="flex flex-row">
						<CopyIcon
							className="h-4 w-4 text-textSecondaryLight hover:cursor-pointer dark:text-textSecondaryDark"
							onClick={() => {
								navigator.clipboard.writeText(
									collection.references.attestationArweaveTxId
										? collection.references.attestationArweaveTxId
										: '',
								)
								tooltipCopyArweave.current?.open({
									anchorSelect: '#copyArweave',
									content: 'Copied!',
								})
								setTimeout(() => {
									tooltipCopyArweave.current?.close()
								}, 2000)
							}}
						/>
						<Tooltip imperativeModeOnly ref={tooltipCopyArweave} />
					</div>
				</div>
			)}
			{collection.isRevoked && (
				<div className="mt-4 flex flex-row items-center gap-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
					>
						Status:
					</TypographyLabel>
					<CalloutWarningIcon />
					<TypographyLabel size="medium">Revoked</TypographyLabel>
				</div>
			)}
			{!collection.isRevoked && (
				<>
					<div className="mt-4 flex flex-row items-center gap-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
						<TypographyLabel
							size="medium"
							isBold={true}
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap"
						>
							Status:
						</TypographyLabel>
						<CalloutSuccessIcon />
						<TypographyLabel size="medium">Completed</TypographyLabel>
					</div>
					{!collection.isRevoked && (
						<div className="mt-16 flex h-full w-full flex-col rounded border border-textInactiveLight bg-uiTertiaryLight dark:border-textInactiveDark dark:bg-uiTertiaryDark">
							<CollectionTemplate collection={collection} />
						</div>
					)}
					<div className="mt-16 flex flex-1 items-center gap-x-4">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Associated NFTs
						</TypographyHeading>
						<Counter size="small" children={nftCount} />
					</div>
					<div className="pt-6">
						{nfts.length > 0 ? (
							<CIDsTable data={createCIDTableData(collection, nfts)} />
						) : (
							<TypographyParagraph
								size="medium"
								isBold={false}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								The Collection is still indexing please reload the page to see
								NFTs appear here: {nftMessage}
							</TypographyParagraph>
						)}
					</div>
					<div className="mt-16 flex flex-1 items-center gap-x-4">
						<TypographyHeading
							size="medium"
							isBold={true}
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Signers
						</TypographyHeading>
						<Counter size="small" children={1} />
					</div>
					<div className="pt-6">
						<SignersTable data={mapSigners(collection)}></SignersTable>
					</div>
				</>
			)}
		</div>
	)
}
